import { useTranslation } from "react-i18next";
import { Checkbox } from "@tellescope/ui";
import { Select, Label, Input } from "@tellescope/design-system";
import type { ChatRoomListBlock } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import { ItemFilterSection } from "../shared/ItemFilterSection";
import type { BlockTypeEditorProps } from "../types";
import type { VisibilityCondition } from "@tellescope/visibility-schema";

type ChatRoomListBlockEditorProps = BlockTypeEditorProps<ChatRoomListBlock>;

export function ChatRoomListBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: ChatRoomListBlockEditorProps) {
  const { t } = useTranslation();

  const updateItemFilter = (condition: VisibilityCondition | undefined) => {
    if (condition) {
      updateProps({ itemFilter: { condition } });
    } else {
      updateProps({ itemFilter: undefined });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Max Items to Show")}</Label>
        <Select
          value={String(block.props.maxItems ?? 5)}
          onValueChange={(value) => updateProps({ maxItems: parseInt(value, 10) })}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="1">1</Select.Item>
            <Select.Item value="2">2</Select.Item>
            <Select.Item value="3">3</Select.Item>
            <Select.Item value="5">5</Select.Item>
            <Select.Item value="10">10</Select.Item>
          </Select.Content>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("Empty Message (optional)")}</Label>
        <Input
          value={block.props.emptyMessage || ""}
          onChange={(e) => updateProps({ emptyMessage: e.target.value || undefined })}
          placeholder={t("No messages")}
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id="allowNewConversation"
            checked={block.props.allowNewConversation ?? false}
            onCheckedChange={(checked) => updateProps({ allowNewConversation: !!checked })}
          />
          <label htmlFor="allowNewConversation" className="text-sm cursor-pointer">
            {t("Allow New Conversation")}
          </label>
        </div>
        <p className="text-xs text-muted-foreground">{t("Allow patients to start new chat conversations")}</p>
      </div>

      {block.props.allowNewConversation && (
        <div className="space-y-1 pl-6">
          <div className="flex items-center gap-2">
            <Checkbox
              id="allowCareTeamSelection"
              checked={block.props.allowCareTeamSelection ?? false}
              onCheckedChange={(checked) => updateProps({ allowCareTeamSelection: !!checked || undefined })}
            />
            <label htmlFor="allowCareTeamSelection" className="text-sm cursor-pointer">
              {t("Let patients choose a care team member")}
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            {t(
              "When starting a new conversation, patients pick which care team member to message. If they have no care team assigned, the message goes to your general inbox as before."
            )}
          </p>
        </div>
      )}

      <ItemFilterSection
        itemFilter={block.props.itemFilter}
        onChange={updateItemFilter}
        description={t(
          "Filter which messages are displayed based on conditions. Use 'Chat Rooms' source for conversation properties."
        )}
      />

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
